import { queryOptions, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api, unwrap, type Schemas } from '@/lib/api/client';
import { refreshAccessToken } from '@/lib/auth/auth-fetch';
import { dayBefore, type DateWindow } from '@/lib/format/format';
import { eventKeys } from '@/features/events/api';
import { organizationKeys } from '@/features/organizations/api';
import { canManageEvents, canManageMembers, type EventTab } from './vocab';

export type Organization = Schemas['OrganizationDto'];
export type OrganizationInput = Schemas['OrganizationRequest'];
export type OrganizationEvent = Schemas['RaceEventDto'];
export type OrganizationEventPage = Schemas['PagedResultOfRaceEventDto'];
export type EventInput = Schemas['RaceEventRequest'];
export type Race = Schemas['RaceDto'];
export type RaceInput = Schemas['RaceRequest'];
export type InterestedRunner = Schemas['InterestedRunnerDto'];
export type Member = Schemas['MemberDto'];

export type EventSort = 'date' | 'name' | 'city';

export interface OrganizationEventsQuery {
  /** Inclusive event dates; null matches nothing, so nothing is asked. */
  window?: DateWindow | null;
  /** Name or city, without diacritics (not across scripts). */
  keyword?: string;
  /** One race category, in the API's words (the design system's). */
  category?: string;
  sort?: EventSort;
  descending?: boolean;
  page?: number;
  size?: number;
}

const SORT_FIELDS: Record<EventSort, string> = {
  date: 'DateTimeLocal',
  name: 'Name',
  city: 'City',
};

export const organizerKeys = {
  mine: [...organizationKeys.all, 'mine'] as const,
  /** Everything read under one organization's events: lists, counts and interest. */
  events: (organizationId: string) => ['organization-events', organizationId] as const,
  eventList: (organizationId: string, query: OrganizationEventsQuery) =>
    [...organizerKeys.events(organizationId), 'list', query] as const,
  interested: (organizationId: string, eventId: string) =>
    [...organizerKeys.events(organizationId), eventId, 'interested'] as const,
  runners: (organizationId: string, eventId: string) =>
    [...organizerKeys.interested(organizationId, eventId), 'all'] as const,
  /** Everything read about one event: itself, its races and the interest in it. */
  eventScope: (organizationId: string, eventId: string) =>
    [...organizerKeys.events(organizationId), eventId] as const,
  event: (organizationId: string, eventId: string) =>
    [...organizerKeys.events(organizationId), eventId, 'event'] as const,
  races: (organizationId: string, eventId: string) =>
    [...organizerKeys.events(organizationId), eventId, 'races'] as const,
  members: (organizationId: string) => ['organization-members', organizationId] as const,
};

const collator = new Intl.Collator('sr-Latn', { sensitivity: 'base' });

/**
 * The organizations the signed-in person belongs to, A–Z. It's the membership list, and its
 * status is read from the database, so it's fresher than the token's organization claim.
 */
export const myOrganizationsQuery = () =>
  queryOptions({
    queryKey: organizerKeys.mine,
    queryFn: async () => {
      const mine = await unwrap(api.GET('/api/organizations/my'));
      return [...mine].sort((a, b) => collator.compare(a.name, b.name));
    },
  });

/** Creates the organization, then refreshes the token so its membership claim arrives. */
export function useCreateOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: OrganizationInput) => {
      const id = await unwrap(api.POST('/api/organizations', { body: input }));
      await refreshAccessToken();
      return id;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: organizationKeys.all }),
  });
}

/**
 * Changes the name and description (the owner's, at any status). The membership list carries
 * them, so the shell and switcher read it again.
 */
export function useUpdateOrganization(organizationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Pick<OrganizationInput, 'name' | 'description'>) =>
      unwrap(
        api.PUT('/api/organizations/{organizationId}', {
          params: { path: { organizationId } },
          body: input,
        }),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: organizationKeys.all }),
  });
}

/**
 * Deletes the organization (the owner's, once it has no events), then refreshes the token so its
 * claim goes. Like leaving, the page goes elsewhere before `forgetOrganization`.
 */
export function useDeleteOrganization(organizationId: string) {
  return useMutation({
    mutationFn: async () => {
      await unwrap(
        api.DELETE('/api/organizations/{organizationId}', {
          params: { path: { organizationId } },
        }),
      );
      await refreshAccessToken().catch(() => undefined);
    },
  });
}

type Scoped = Pick<Organization, 'id' | 'status'>;

/**
 * The organization's events, by date (soonest first) unless sorted otherwise. Only an approved
 * organization asks: the API refuses every other status.
 */
export const organizationEventsQuery = (organization: Scoped, query: OrganizationEventsQuery) =>
  queryOptions({
    queryKey: organizerKeys.eventList(organization.id, query),
    queryFn: (): Promise<OrganizationEventPage> =>
      query.window === null
        ? Promise.resolve({
            items: [],
            totalCount: 0,
            pageNumber: query.page ?? 1,
            pageSize: query.size ?? 20,
          })
        : unwrap(
            api.GET('/api/organizations/{organizationId}/events', {
              params: {
                path: { organizationId: organization.id },
                query: {
                  startDate: query.window?.start,
                  endDate: query.window?.end,
                  keyword: query.keyword,
                  category: query.category,
                  sortBy: SORT_FIELDS[query.sort ?? 'date'],
                  sortDirection: query.descending ? 'desc' : 'asc',
                  pageNumber: query.page ?? 1,
                  pageSize: query.size ?? 20,
                },
              },
            }),
          ),
    // The previous view stays while the next loads, but never another organization's.
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[1] === organization.id ? previous : undefined,
    enabled: canManageEvents(organization),
  });

export const UPCOMING_ON_OVERVIEW = 5;

/** Upcoming from today (Belgrade), so an event today is upcoming; past until yesterday. */
export const tabWindow = (tab: EventTab, today: string): DateWindow =>
  tab === 'upcoming' ? { start: today } : { end: dayBefore(today) };

/** The next few and the total. */
export const upcomingEventsQuery = (organization: Scoped, today: string) =>
  organizationEventsQuery(organization, {
    window: tabWindow('upcoming', today),
    size: UPCOMING_ON_OVERVIEW,
  });

/** Latest first: the total and the most recent one. */
export const pastEventsQuery = (organization: Scoped, today: string) =>
  organizationEventsQuery(organization, {
    window: tabWindow('past', today),
    descending: true,
    size: 1,
  });

/** The organization from the cached membership list if it's approved, for a loader's prefetch. */
export function cachedApprovedOrganization(
  queryClient: QueryClient,
  organizationId: string,
): Organization | undefined {
  const organization = queryClient
    .getQueryData(myOrganizationsQuery().queryKey)
    ?.find((o) => o.id === organizationId);
  return organization && canManageEvents(organization) ? organization : undefined;
}

/** How many runners marked the event as interested in the app. */
export const interestedCountQuery = (organization: Scoped, eventId: string | undefined) =>
  queryOptions({
    queryKey: organizerKeys.interested(organization.id, eventId ?? ''),
    queryFn: () =>
      unwrap(
        api.GET('/api/organizations/{organizationId}/events/{raceEventId}/interested-runners', {
          params: {
            path: { organizationId: organization.id, raceEventId: eventId ?? '' },
            query: { pageSize: 1 },
          },
        }),
      ).then((page) => page.totalCount),
    enabled: !!eventId && canManageEvents(organization),
  });

/** The API's largest page. */
const RUNNERS_PER_REQUEST = 100;

/**
 * Everyone who marked the event as interested, every page, to search, sort and export in the
 * browser. The API's order ends on the interest's id, so no page repeats or skips anyone.
 */
export const interestedRunnersQuery = (organization: Scoped, eventId: string) =>
  queryOptions({
    queryKey: organizerKeys.runners(organization.id, eventId),
    queryFn: async (): Promise<InterestedRunner[]> => {
      const runners = new Map<string, InterestedRunner>();
      for (let page = 1; ; page++) {
        const result = await unwrap(
          api.GET('/api/organizations/{organizationId}/events/{raceEventId}/interested-runners', {
            params: {
              path: { organizationId: organization.id, raceEventId: eventId },
              query: { pageNumber: page, pageSize: RUNNERS_PER_REQUEST },
            },
          }),
        );
        for (const runner of result.items) runners.set(runner.interestId, runner);
        if (
          result.items.length < RUNNERS_PER_REQUEST ||
          page * RUNNERS_PER_REQUEST >= result.totalCount
        )
          break;
      }
      return [...runners.values()];
    },
    enabled: canManageEvents(organization),
  });

/** One of the organization's events. Another organization's event reads as missing (404). */
export const organizationEventQuery = (organization: Scoped, eventId: string) =>
  queryOptions({
    queryKey: organizerKeys.event(organization.id, eventId),
    queryFn: () =>
      unwrap(
        api.GET('/api/organizations/{organizationId}/events/{raceEventId}', {
          params: { path: { organizationId: organization.id, raceEventId: eventId } },
        }),
      ),
    enabled: canManageEvents(organization),
  });

/** The event's races, shortest first (the API's order). */
export const eventRacesQuery = (organization: Scoped, eventId: string) =>
  queryOptions({
    queryKey: organizerKeys.races(organization.id, eventId),
    queryFn: () =>
      unwrap(
        api.GET('/api/organizations/{organizationId}/events/{raceEventId}/races', {
          params: { path: { organizationId: organization.id, raceEventId: eventId } },
        }),
      ),
    enabled: canManageEvents(organization),
  });

/** Reads the organization's events again, and the catalog, which lists them too. */
export function useEventsChanged(organizationId: string) {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: organizerKeys.events(organizationId) }),
      queryClient.invalidateQueries({ queryKey: eventKeys.all }),
    ]);
}

/** Adds an event to the organization; resolves to its id. */
export function useCreateEvent(organizationId: string) {
  const changed = useEventsChanged(organizationId);
  return useMutation({
    mutationFn: (input: EventInput) =>
      unwrap(
        api.POST('/api/organizations/{organizationId}/events', {
          params: { path: { organizationId } },
          body: input,
        }),
      ),
    onSuccess: changed,
  });
}

export function useUpdateEvent(organizationId: string) {
  const changed = useEventsChanged(organizationId);
  return useMutation({
    mutationFn: ({ eventId, input }: { eventId: string; input: EventInput }) =>
      unwrap(
        api.PUT('/api/organizations/{organizationId}/events/{raceEventId}', {
          params: { path: { organizationId, raceEventId: eventId } },
          body: input,
        }),
      ),
    onSuccess: changed,
  });
}

/** Marks the event cancelled; the app keeps it with a badge and interested runners are told. */
export function useCancelEvent(organizationId: string) {
  const changed = useEventsChanged(organizationId);
  return useMutation({
    mutationFn: (eventId: string) =>
      unwrap(
        api.POST('/api/organizations/{organizationId}/events/{raceEventId}/cancel', {
          params: { path: { organizationId, raceEventId: eventId } },
        }),
      ),
    // A refusal means the event moved on (another tab), so the page reads it again too.
    onSettled: changed,
  });
}

/** Takes the cancellation back; interested runners are told it is on again. */
export function useReinstateEvent(organizationId: string) {
  const changed = useEventsChanged(organizationId);
  return useMutation({
    mutationFn: (eventId: string) =>
      unwrap(
        api.POST('/api/organizations/{organizationId}/events/{raceEventId}/reinstate', {
          params: { path: { organizationId, raceEventId: eventId } },
        }),
      ),
    onSettled: changed,
  });
}

/** Adds a race to the event; resolves to its id. */
export function useCreateRace(organizationId: string, eventId: string) {
  const changed = useEventsChanged(organizationId);
  return useMutation({
    mutationFn: (input: RaceInput) =>
      unwrap(
        api.POST('/api/organizations/{organizationId}/events/{raceEventId}/races', {
          params: { path: { organizationId, raceEventId: eventId } },
          body: input,
        }),
      ),
    onSuccess: changed,
  });
}

export function useUpdateRace(organizationId: string, eventId: string) {
  const changed = useEventsChanged(organizationId);
  return useMutation({
    mutationFn: ({ raceId, input }: { raceId: string; input: RaceInput }) =>
      unwrap(
        api.PUT('/api/organizations/{organizationId}/events/{raceEventId}/races/{raceId}', {
          params: { path: { organizationId, raceEventId: eventId, raceId } },
          body: input,
        }),
      ),
    onSuccess: changed,
  });
}

export function useDeleteRace(organizationId: string, eventId: string) {
  const changed = useEventsChanged(organizationId);
  return useMutation({
    mutationFn: (raceId: string) =>
      unwrap(
        api.DELETE('/api/organizations/{organizationId}/events/{raceEventId}/races/{raceId}', {
          params: { path: { organizationId, raceEventId: eventId, raceId } },
        }),
      ),
    onSuccess: changed,
  });
}

/**
 * Deletes the event with its races. Nothing is read again here: the page leaves first, then
 * calls `forgetEvent`, so the gone event is never asked for.
 */
export function useDeleteEvent(organizationId: string) {
  return useMutation({
    mutationFn: (eventId: string) =>
      unwrap(
        api.DELETE('/api/organizations/{organizationId}/events/{raceEventId}', {
          params: { path: { organizationId, raceEventId: eventId } },
        }),
      ),
  });
}

/** Moves the event to another organization; like delete, the page leaves before `forgetEvent`. */
export function useTransferEvent(organizationId: string) {
  return useMutation({
    mutationFn: ({ eventId, targetId }: { eventId: string; targetId: string }) =>
      unwrap(
        api.POST('/api/organizations/{organizationId}/events/{raceEventId}/transfer', {
          params: { path: { organizationId, raceEventId: eventId } },
          body: { targetOrganizationId: targetId },
        }),
      ),
  });
}

/** After an event left the organization: drops what was read about it and reads the lists again. */
export function useForgetEvent() {
  const queryClient = useQueryClient();
  return (organizationId: string, eventId: string, alsoOrganizationId?: string) => {
    queryClient.removeQueries({ queryKey: organizerKeys.eventScope(organizationId, eventId) });
    return Promise.all([
      queryClient.invalidateQueries({ queryKey: organizerKeys.events(organizationId) }),
      alsoOrganizationId
        ? queryClient.invalidateQueries({ queryKey: organizerKeys.events(alsoOrganizationId) })
        : null,
      queryClient.invalidateQueries({ queryKey: eventKeys.all }),
    ]);
  };
}

/** The API's largest page. */
const MEMBERS_PER_REQUEST = 100;

/**
 * Everyone in the organization, every page, in the order they joined. Only an approved
 * organization asks: the API refuses every other status.
 */
export const membersQuery = (organization: Scoped) =>
  queryOptions({
    queryKey: organizerKeys.members(organization.id),
    queryFn: async (): Promise<Member[]> => {
      const members = new Map<string, Member>();
      for (let page = 1; ; page++) {
        const result = await unwrap(
          api.GET('/api/organizations/{organizationId}/members', {
            params: {
              path: { organizationId: organization.id },
              query: { pageNumber: page, pageSize: MEMBERS_PER_REQUEST },
            },
          }),
        );
        for (const member of result) members.set(member.id, member);
        if (result.length < MEMBERS_PER_REQUEST) break;
      }
      return [...members.values()];
    },
    enabled: canManageMembers(organization),
  });

/** Reads the members again, and the membership list, which carries the count and the owner. */
function useMembersChanged(organizationId: string) {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: organizerKeys.members(organizationId) }),
      queryClient.invalidateQueries({ queryKey: organizerKeys.mine }),
    ]);
}

/** Adds the account whose confirmed email this is; resolves to the new member. */
export function useAddMember(organizationId: string) {
  const changed = useMembersChanged(organizationId);
  return useMutation({
    mutationFn: (email: string) =>
      unwrap(
        api.POST('/api/organizations/{organizationId}/members/by-email', {
          params: { path: { organizationId } },
          body: { email },
        }),
      ),
    onSuccess: changed,
  });
}

/** Removes a member; the owner removes others, a member removes themselves (see `useLeave`). */
export function useRemoveMember(organizationId: string) {
  const changed = useMembersChanged(organizationId);
  return useMutation({
    mutationFn: (memberId: string) =>
      unwrap(
        api.DELETE('/api/organizations/{organizationId}/members/{memberId}', {
          params: { path: { organizationId, memberId } },
        }),
      ),
    onSuccess: changed,
  });
}

/** Hands the organization over to another member; the owner stays a member. */
export function useMakeOwner(organizationId: string) {
  const changed = useMembersChanged(organizationId);
  return useMutation({
    mutationFn: (memberId: string) =>
      unwrap(
        api.PUT('/api/organizations/{organizationId}/owner', {
          params: { path: { organizationId } },
          body: { memberId },
        }),
      ),
    onSuccess: changed,
  });
}

/**
 * Removes the person's own membership. Nothing is read again here: the page leaves the
 * organization first, then calls `forgetOrganization`, so nothing asks for it again.
 */
export function useLeave(organizationId: string) {
  return useMutation({
    mutationFn: async (memberId: string) => {
      await unwrap(
        api.DELETE('/api/organizations/{organizationId}/members/{memberId}', {
          params: { path: { organizationId, memberId } },
        }),
      );
      // The token's organization claim goes with the next refresh; take it now.
      await refreshAccessToken().catch(() => undefined);
    },
  });
}

/** After the person left an organization: drops what was read under it and reads the list again. */
export function useForgetOrganization() {
  const queryClient = useQueryClient();
  return (organizationId: string) => {
    queryClient.removeQueries({ queryKey: organizerKeys.members(organizationId) });
    queryClient.removeQueries({ queryKey: organizerKeys.events(organizationId) });
    return queryClient.invalidateQueries({ queryKey: organizerKeys.mine });
  };
}
